// Pure pivots over the Gantt rows returned by /schedulerView/:id/:iter.
// Drives the Details / FG / Input / Kitchen / Line utilisation tabs without
// any new API calls — the schedule output already carries every field we need.

export type GanttRow = {
  id: string
  kitchen: string
  line: string
  productCode: string
  product_description?: string
  base_material: string
  base_material_description?: string

  kitchen_start_time?: number
  kitchen_end_time?: number
  kitchen_clean_start_time?: number | null
  kitchen_clean_end_time?: number | null
  kitchen_preclean_start_time?: number | null
  kitchen_preclean_end_time?: number | null

  line_start_time?: number
  line_end_time?: number
  line_clean_start_time?: number | null
  line_clean_end_time?: number | null
  line_preclean_start_time?: number | null
  line_preclean_end_time?: number | null

  additional_kitchen_cips?: string | null
  additional_line_cips?: string | null

  runRateBase?: number | null
  runRateLine?: number | null
  pr?: number | null
  quantity?: number
  weekNumber?: number
}

export type DetailsRow = {
  id: string
  productCodeLineKitchen: string
  kitchen: string
  line: string
  productCode: string
  product_description: string
  code_desc: string
  inputProductCode: string
  inputProductName: string
  inputCode_desc: string
  max_kitchen_clean_end_time: number
  run_rate: number
  pr: number
  sum_quantity: number
  sum_base_material_qty: number
  sum_kitchen_run_time: number
  sum_kitchen_run_time_unique: number
  sum_kitchen_clean_time: number
  sum_line_run_time: number
  sum_line_clean_time: number
}

const num = (v: number | null | undefined): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : 0

/** Sum of (end − start) across a JSON-stringified CipEvent array. */
const cipDurationSum = (json: string | null | undefined): number => {
  if (!json) return 0
  try {
    const arr = JSON.parse(json) as Array<{ start: number; end: number }>
    return arr.reduce(
      (acc, e) => acc + Math.max(0, num(e.end) - num(e.start)),
      0
    )
  } catch {
    return 0
  }
}

const composeDesc = (code: string, desc: string | undefined): string => {
  if (!desc || desc === code) return code
  return `${code}_${desc}`
}

/**
 * Convert each Gantt row into a Details row. Kitchen run time is deduped via
 * `_unique` — the first row per (kitchen, start, end) claims the full hours;
 * subsequent rows show 0 so totals don't double-count shared kitchen runs.
 */
export const toDetails = (rows: GanttRow[]): DetailsRow[] => {
  const seenKitchenRun = new Set<string>()
  return rows.map((r) => {
    const kStart = num(r.kitchen_start_time)
    const kEnd = num(r.kitchen_end_time)
    const lStart = num(r.line_start_time)
    const lEnd = num(r.line_end_time)
    const kRun = Math.max(0, kEnd - kStart)
    const lRun = Math.max(0, lEnd - lStart)
    const kClean = cipDurationSum(r.additional_kitchen_cips)
    const lClean = cipDurationSum(r.additional_line_cips)

    const kKey = `${r.kitchen}|${kStart}|${kEnd}`
    const isFirstForKitchenRun = !seenKitchenRun.has(kKey)
    if (isFirstForKitchenRun) seenKitchenRun.add(kKey)

    const baseMatQty = kRun * num(r.runRateBase)
    const productCodeLineKitchen = `${r.productCode}_${r.line}_${r.kitchen}`

    return {
      id: r.id,
      productCodeLineKitchen,
      kitchen: r.kitchen,
      line: r.line,
      productCode: r.productCode,
      product_description: r.product_description ?? '',
      code_desc: composeDesc(r.productCode, r.product_description),
      inputProductCode: r.base_material,
      inputProductName: r.base_material_description ?? '',
      inputCode_desc: composeDesc(r.base_material, r.base_material_description),
      max_kitchen_clean_end_time: num(r.kitchen_clean_end_time),
      run_rate: num(r.runRateLine),
      pr: num(r.pr),
      sum_quantity: num(r.quantity),
      sum_base_material_qty: baseMatQty,
      sum_kitchen_run_time: kRun,
      sum_kitchen_run_time_unique: isFirstForKitchenRun ? kRun : 0,
      sum_kitchen_clean_time: kClean,
      sum_line_run_time: lRun,
      sum_line_clean_time: lClean,
    }
  })
}

// ─── Aggregations ────────────────────────────────────────────────────────────

export type FGRow = {
  id: string
  code_desc: string
  inputCode_desc: string
  sum_quantity: number
  sum_base_material_qty: number
  sum_kitchen_run_time: number
  sum_kitchen_clean_time: number
  sum_line_run_time: number
  sum_line_clean_time: number
  sum_kitchen_run_time_unique: number
}

/** Group Details by (Code_Desc, InputCode_Desc) and sum the numerics. */
export const aggregateFG = (rows: DetailsRow[]): FGRow[] => {
  const map = new Map<string, FGRow>()
  for (const r of rows) {
    const key = `${r.code_desc}||${r.inputCode_desc}`
    let bucket = map.get(key)
    if (!bucket) {
      bucket = {
        id: key,
        code_desc: r.code_desc,
        inputCode_desc: r.inputCode_desc,
        sum_quantity: 0,
        sum_base_material_qty: 0,
        sum_kitchen_run_time: 0,
        sum_kitchen_clean_time: 0,
        sum_line_run_time: 0,
        sum_line_clean_time: 0,
        sum_kitchen_run_time_unique: 0,
      }
      map.set(key, bucket)
    }
    bucket.sum_quantity += r.sum_quantity
    bucket.sum_base_material_qty += r.sum_base_material_qty
    bucket.sum_kitchen_run_time += r.sum_kitchen_run_time
    bucket.sum_kitchen_clean_time += r.sum_kitchen_clean_time
    bucket.sum_line_run_time += r.sum_line_run_time
    bucket.sum_line_clean_time += r.sum_line_clean_time
    bucket.sum_kitchen_run_time_unique += r.sum_kitchen_run_time_unique
  }
  return Array.from(map.values()).sort((a, b) =>
    a.code_desc.localeCompare(b.code_desc)
  )
}

export type InputAggRow = {
  id: string
  inputCode_desc: string
  sum_base_material_qty: number
  sum_kitchen_run_time: number
  sum_kitchen_run_time_unique: number
  sum_kitchen_clean_time: number
}

/** Group by InputCode_Desc only — for the Input tab. */
export const aggregateInput = (rows: DetailsRow[]): InputAggRow[] => {
  const map = new Map<string, InputAggRow>()
  for (const r of rows) {
    let bucket = map.get(r.inputCode_desc)
    if (!bucket) {
      bucket = {
        id: r.inputCode_desc,
        inputCode_desc: r.inputCode_desc,
        sum_base_material_qty: 0,
        sum_kitchen_run_time: 0,
        sum_kitchen_run_time_unique: 0,
        sum_kitchen_clean_time: 0,
      }
      map.set(r.inputCode_desc, bucket)
    }
    bucket.sum_base_material_qty += r.sum_base_material_qty
    bucket.sum_kitchen_run_time += r.sum_kitchen_run_time
    bucket.sum_kitchen_run_time_unique += r.sum_kitchen_run_time_unique
    bucket.sum_kitchen_clean_time += r.sum_kitchen_clean_time
  }
  return Array.from(map.values()).sort((a, b) =>
    a.inputCode_desc.localeCompare(b.inputCode_desc)
  )
}

export type KitchenAggRow = {
  id: string
  kitchen: string
  inputCode_desc: string
  fg_cases: number
  input_material_kg: number
  max_kitchen_clean_end_time: number
  sum_kitchen_run_time: number
  sum_kitchen_run_time_unique: number
  sum_kitchen_clean_time: number
  sum_line_run_time: number
  sum_line_clean_time: number
}

/** Group by (kitchen, InputCode_Desc) — for the Kitchen tab. */
export const aggregateKitchen = (rows: DetailsRow[]): KitchenAggRow[] => {
  const map = new Map<string, KitchenAggRow>()
  for (const r of rows) {
    const key = `${r.kitchen}||${r.inputCode_desc}`
    let bucket = map.get(key)
    if (!bucket) {
      bucket = {
        id: key,
        kitchen: r.kitchen,
        inputCode_desc: r.inputCode_desc,
        fg_cases: 0,
        input_material_kg: 0,
        max_kitchen_clean_end_time: 0,
        sum_kitchen_run_time: 0,
        sum_kitchen_run_time_unique: 0,
        sum_kitchen_clean_time: 0,
        sum_line_run_time: 0,
        sum_line_clean_time: 0,
      }
      map.set(key, bucket)
    }
    bucket.fg_cases += r.sum_quantity
    bucket.input_material_kg += r.sum_base_material_qty
    if (r.max_kitchen_clean_end_time > bucket.max_kitchen_clean_end_time) {
      bucket.max_kitchen_clean_end_time = r.max_kitchen_clean_end_time
    }
    bucket.sum_kitchen_run_time += r.sum_kitchen_run_time
    bucket.sum_kitchen_run_time_unique += r.sum_kitchen_run_time_unique
    bucket.sum_kitchen_clean_time += r.sum_kitchen_clean_time
    bucket.sum_line_run_time += r.sum_line_run_time
    bucket.sum_line_clean_time += r.sum_line_clean_time
  }
  return Array.from(map.values()).sort(
    (a, b) =>
      a.kitchen.localeCompare(b.kitchen) ||
      a.inputCode_desc.localeCompare(b.inputCode_desc)
  )
}

export type LineAggRow = {
  id: string
  line: string
  productCode: string
  cases: number
  sum_line_clean_time: number
  sum_line_run_time: number
}

/** Group by (line, productCode) — for the Line tab. */
export const aggregateLine = (rows: DetailsRow[]): LineAggRow[] => {
  const map = new Map<string, LineAggRow>()
  for (const r of rows) {
    const key = `${r.line}||${r.productCode}`
    let bucket = map.get(key)
    if (!bucket) {
      bucket = {
        id: key,
        line: r.line,
        productCode: r.productCode,
        cases: 0,
        sum_line_clean_time: 0,
        sum_line_run_time: 0,
      }
      map.set(key, bucket)
    }
    bucket.cases += r.sum_quantity
    bucket.sum_line_clean_time += r.sum_line_clean_time
    bucket.sum_line_run_time += r.sum_line_run_time
  }
  return Array.from(map.values()).sort(
    (a, b) =>
      a.line.localeCompare(b.line) || a.productCode.localeCompare(b.productCode)
  )
}
